import { EMPTY_TABLE_MESSAGE } from "@app/shared/constants/empty-table.constants";
import { logger } from "@core/utils/logger";
import { TableAction, TableColumn } from "@corp-ais/omniark-ui";
import { STORAGE_KEYS } from "../../../constants/enrichment.constants";
import { ColumnDef, EnrichmentPriceRow } from "./enrichment-list.types";

/** Widths are what the table lays out by (`table-layout: fixed`); the cells
 *  cap or truncate their content to the same widths. Since v17 the default set
 *  is wider than a 1512px viewport, so the list scrolls sideways on a laptop. */
export const ALL_COLUMNS: ColumnDef[] = [
  { key: "price", label: "PRICE", width: "168px", alwaysOn: true },
  { key: "status", label: "PRICE STATUS", width: "118px", alwaysOn: true },
  { key: "enriched", label: "ENRICHED", width: "130px", alwaysOn: true },
  { key: "package", label: "PRODUCT OFFERING", width: "152px", alwaysOn: true },
  { key: "version", label: "VERSION", width: "90px" },
  { key: "productLine", label: "PRODUCT LINE", width: "94px" },
  { key: "productType", label: "PRODUCT TYPE", width: "94px" },
  { key: "offerClassification", label: "OFFER CLASSIFICATION", width: "110px" },
  { key: "amount", label: "AMOUNT", align: "right", width: "96px" },
  { key: "shelves", label: "SHELVES", width: "150px" },
  { key: "updated", label: "PRICE UPDATED", width: "104px" },
  { key: "validity", label: "VALID FROM / TO", width: "128px" },
  { key: "channels", label: "CHANNELS", width: "130px" },
];

/** enr-table column definitions, in `ALL_COLUMNS` order. Every cell renders
 *  through the `#customCell` slot. None is sortable: the BFF sorts by package
 *  fields only, and a price column must not sort by its package's value. */
export const ENRICHMENT_TABLE_COLUMNS: TableColumn<EnrichmentPriceRow>[] =
  ALL_COLUMNS.map(({ key, label, width, align }) => ({
    key,
    label,
    width,
    align,
    customTemplate: true,
    customHeaderTemplate: true,
  }));

export const PREVIEW_TABLE_ACTION: TableAction<EnrichmentPriceRow> = {
  id: "preview",
  label: "Quick preview",
  icon: "visibility",
};

/** Default `<enr-table>` config for the enrichment list page. */
export const ENRICHMENT_TABLE_CONFIG = {
  pageable: true,
  pageSize: 10,
  pageSizeOptions: [10, 50],
  filterable: true,
  // Default off: the checkbox column is enabled at runtime only for a role
  // holding a bulk action's permission (see
  // EnrichmentList.canSelectRowsForBulkActions / snapToFirstPage).
  selectable: false,
  serverSide: true,
  emptyMessage: EMPTY_TABLE_MESSAGE,
  emptyIcon: "search_off",
  // No fixed scroll height: the list is server-paginated (each page is already
  // bounded by pageSize), so an internal `overflow:auto` viewport adds nothing
  // and triggers a sticky-header scrollHeight recalc bug — the table scrolls
  // into empty space after a sort/re-render. Let the page own the scroll.
};

export const DEFAULT_VISIBLE_COLUMNS: string[] = ALL_COLUMNS.filter(
  (c) => !c.defaultHidden,
).map((c) => c.key);

/** Bump when the default-visible column set or its order changes so existing
 *  users see newly-added columns, and the new default order, instead of being
 *  stuck with a stale saved list. v14 added VERSION, PRODUCT LINE and PRODUCT
 *  TYPE after the product offering; v15 turned SHELVES and CHANNELS off by
 *  default; v16 added ENRICHED; v17 added OFFER CLASSIFICATION and turned
 *  SHELVES and CHANNELS back on. Legacy plain-array saves (no version) are
 *  treated as v0 → reset. */
const COLUMN_PREFERENCES_VERSION = 17;

/** Persist visible-column preferences to localStorage. */
export function saveColumnPreferences(columns: string[]): void {
  localStorage.setItem(
    STORAGE_KEYS.ENRICHMENT_LIST_COLUMNS,
    JSON.stringify({ version: COLUMN_PREFERENCES_VERSION, columns }),
  );
}

/** Read visible-column preferences from localStorage, validating against the
 *  current column catalog. Falls back to {@link DEFAULT_VISIBLE_COLUMNS} when
 *  there is nothing saved, the saved value is malformed, or the saved version
 *  is older than the current schema (so new default columns auto-appear). */
export function loadColumnPreferences(): string[] {
  const saved = localStorage.getItem(STORAGE_KEYS.ENRICHMENT_LIST_COLUMNS);
  if (!saved) return DEFAULT_VISIBLE_COLUMNS;
  try {
    const parsed = JSON.parse(saved);
    // Legacy plain-array format (pre-versioning) → reset so new default
    // columns (e.g., Offer Classification) become visible.
    if (Array.isArray(parsed)) return DEFAULT_VISIBLE_COLUMNS;
    if (
      !parsed ||
      typeof parsed !== "object" ||
      parsed.version !== COLUMN_PREFERENCES_VERSION ||
      !Array.isArray(parsed.columns)
    ) {
      return DEFAULT_VISIBLE_COLUMNS;
    }
    const valid = parsed.columns.filter((col: string) =>
      ALL_COLUMNS.some((c) => c.key === col),
    );
    return valid.length > 0 ? valid : DEFAULT_VISIBLE_COLUMNS;
  } catch (error) {
    logger.warn("Error loading column preferences:", error);
    return DEFAULT_VISIBLE_COLUMNS;
  }
}

/** True when `key` references a column the user is not allowed to hide. */
export function isColumnLocked(key: string): boolean {
  return !!ALL_COLUMNS.find((c) => c.key === key)?.alwaysOn;
}
